import { deleteCookie, hasCookie } from 'cookies-next';
import type { IncomingMessage, ServerResponse } from 'http';
import type { NextApiRequest, NextApiResponse } from 'next';

import {
    AUTH_COOKIE_KEY,
    REGISTRATION_REGION_COOKIE_KEY,
    USER_STATUS_COOKIE_KEY,
} from '@/utils/constants/common';
import { getSharedBranchAuthCookieDomain } from '@/utils/server/shared-branch-auth-cookie-domain';

/**
 * Clears the auth, user-status and registration-region cookies (`CBS_COM`, `CBS_ST`, `CBS_RR`)
 * so that the next request is treated as anonymous by `requireSignIn` middleware and downstream APIs.
 *
 * Called after a successful password reset/change: the backend invalidates the
 * server-side session, but if it does not include a clearing `Set-Cookie` the stale cookies
 * remain in the browser. Middleware then lets the user into `/account`, where user-profiles
 * returns an anonymous profile (e.g. `anonymous_…@anonymous.com`). Clearing the cookies forces
 * middleware to redirect to the configured sign-in route (`/account/check-email/` for PTV,
 * `/account/signin/` for PPLUS) on the post-success navigation.
 *
 * The shared-branch cookie domain is matched against the request so platypus feature instances
 * delete the cookie at the same scope used when it was set.
 */
export function clearAuthAndStatusCookies({
    req,
    res,
}: {
    req: NextApiRequest;
    res: NextApiResponse;
}): void {
    const sharedDomain = getSharedBranchAuthCookieDomain(req);
    const cookieOpts = {
        req,
        res,
        ...(sharedDomain ? { domain: sharedDomain } : {}),
    };

    if (hasCookie(AUTH_COOKIE_KEY, cookieOpts)) {
        deleteCookie(AUTH_COOKIE_KEY, cookieOpts);
    }

    if (hasCookie(USER_STATUS_COOKIE_KEY, cookieOpts)) {
        deleteCookie(USER_STATUS_COOKIE_KEY, cookieOpts);
    }

    if (hasCookie(REGISTRATION_REGION_COOKIE_KEY, cookieOpts)) {
        deleteCookie(REGISTRATION_REGION_COOKIE_KEY, cookieOpts);
    }
}

/**
 * SSR counterpart of the clearing user-status-api does on its own response (same three cookies).
 * During getServerSideProps the user prefetch is a server-to-server hop, so any
 * clearing `Set-Cookie` the GraphQL handler emits (invalid session, MVPD
 * DMA-dispute lockout) dies inside that hop and the browser keeps sending the
 * dead cookie. Re-apply it on the page response when the request carried an
 * auth cookie but the server answered with no user. A rejected fetch never gets
 * here, so a transient upstream failure leaves a still-valid session intact.
 */
export function clearAuthAndStatusCookiesIfSessionGone({
    req,
    res,
    userResp,
}: {
    req: IncomingMessage;
    res?: ServerResponse;
    userResp: { userFullProfile?: unknown } | null | undefined;
}): void {
    if (
        !res ||
        !userResp ||
        userResp.userFullProfile !== null ||
        !hasCookie(AUTH_COOKIE_KEY, { req })
    ) {
        return;
    }
    clearAuthAndStatusCookies({
        req: req as NextApiRequest,
        res: res as NextApiResponse,
    });
}
